/**
 * useHarvestMonitoring Hook
 * Custom hook for managing harvest monitoring data and state
 */

import { useState, useCallback } from 'react';
import { HarvestMonitoringApiService } from '../services/harvestMonitoringApiService';
import type { HarvestMonitoringEstateRecord, HarvestMonitoringFilters, SPBEstateRecord, HarvestMonitoringSubtotal } from '../types';
import dayjs from 'dayjs';

export const useHarvestMonitoring = () => {
    const [data, setData] = useState<HarvestMonitoringEstateRecord[]>([]);
    const [spbData, setSpbData] = useState<SPBEstateRecord[]>([]);
    const [totals, setTotals] = useState<HarvestMonitoringSubtotal | null>(null);
    const [viewMode, setViewMode] = useState<'block' | 'spb'>('block');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initialize with today's date
    const today = dayjs().format('YYYYMMDD');
    const [filters, setFilters] = useState<HarvestMonitoringFilters>({
        current_date: today,
        start_date: today,
        end_date: today,
    });

    const fetchData = useCallback(async (newFilters?: Partial<HarvestMonitoringFilters>, mode: 'block' | 'spb' = viewMode) => {
        setLoading(true);
        setError(null);

        try {
            const appliedFilters = newFilters ? { ...filters, ...newFilters } : filters;
            
            if (mode === 'block') {
                const response = await HarvestMonitoringApiService.getMonitoring(appliedFilters);

                if (response.status === 'success') {
                    setData(response.data || []);
                    setTotals(response.totals as HarvestMonitoringSubtotal || null);
                    if (newFilters) {
                        setFilters(appliedFilters);
                    }
                } else {
                    throw new Error(response.message || 'Failed to fetch data');
                }
            } else {
                const response = await HarvestMonitoringApiService.getMonitoringSPB(appliedFilters);

                if (response.status === 'success') {
                    setSpbData(response.data || []);
                    setTotals(response.totals || null);
                    if (newFilters) {
                        setFilters(appliedFilters);
                    }
                } else {
                    throw new Error(response.message || 'Failed to fetch data');
                }
            }
            setViewMode(mode);
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.response?.data?.detail || err.message || 'Failed to fetch harvest monitoring data';
            setError(errorMessage);
            setData([]);
            setSpbData([]);
            setTotals(null);
        } finally {
            setLoading(false);
        }
    }, [filters, viewMode]);

    return {
        data,
        spbData,
        totals,
        viewMode,
        setViewMode,
        loading,
        error,
        filters,
        fetchData,
    };
};
